if (!customElements.get('tiger-discount-popup')) {
  class TigerDiscountPopup extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-popup-overlay]');
      this.dialog = this.querySelector('[data-popup-dialog]');
      this.form = this.closest('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.errorMessage = this.querySelector('[data-popup-error]');
      this.offerState = this.querySelector('[data-offer-state]');
      this.successState = this.querySelector('[data-success-state]');
      this.copyButton = this.querySelector('[data-copy-code]');
      this.storageKey = this.dataset.storageKey || 'tiger-discount-popup';
      this.dismissKey = `${this.storageKey}:dismissed-until`;
      this.claimedKey = `${this.storageKey}:claimed-until`;
      this.viewsKey = `${this.storageKey}:page-views`;
      this.sessionShownKey = `${this.storageKey}:shown`;
      this.sessionDismissedKey = `${this.storageKey}:dismissed`;
      this.lastFocusedElement = null;
      this.cleanupCallbacks = [];

      if (!this.overlay || !this.dialog) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onManualOpen = this.onManualOpen.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.querySelectorAll('[data-popup-close]').forEach((button) => {
        button.addEventListener('click', () => this.close('close'));
      });
      this.querySelector('[data-popup-decline]')?.addEventListener('click', () => this.close('decline'));
      this.overlay.addEventListener('click', this.onOverlayClick);
      this.form?.addEventListener('submit', this.onSubmit);
      this.copyButton?.addEventListener('click', () => this.copyCode());
      window.addEventListener('tiger:discount-popup:open', this.onManualOpen);

      const hasFormError = Boolean(this.errorMessage && !this.errorMessage.hidden);
      const hasClaimed = this.successState && !this.successState.hidden;

      if (hasClaimed) {
        this.recordClaimed();
        this.open(true);
        return;
      }

      if (hasFormError) {
        this.open(true);
        return;
      }

      if (this.dataset.testMode === 'true' || window.Shopify?.designMode) {
        if (this.getSession(this.sessionShownKey) !== 'true') this.open(true);
        return;
      }

      this.incrementPageViews();
      if (!this.isEligible()) return;
      this.setupTriggers();
    }

    disconnectedCallback() {
      this.clearTriggers();
      window.removeEventListener('tiger:discount-popup:open', this.onManualOpen);
      this.form?.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKeydown);
    }

    isEligible() {
      if (this.dataset.enabled !== 'true' || this.dataset.pageAllowed !== 'true') return false;
      if (!this.matchesDevice()) return false;
      if (this.isExcludedPath()) return false;
      if (this.dataset.customerSubscriber === 'true' && this.dataset.showToSubscribers !== 'true') return false;
      if (this.getStoredNumber(this.claimedKey) > Date.now()) return false;
      if (this.getStoredNumber(this.dismissKey) > Date.now()) return false;
      if (this.getSession(this.sessionDismissedKey) === 'true') return false;
      if (this.getSession(this.sessionShownKey) === 'true') return false;

      const minimumViews = Number(this.dataset.minPageViews) || 1;
      return this.getStoredNumber(this.viewsKey) >= minimumViews;
    }

    matchesDevice() {
      const scope = this.dataset.device || 'all';
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (scope === 'mobile') return isMobile;
      if (scope === 'desktop') return !isMobile;
      return true;
    }

    isExcludedPath() {
      const excludedPaths = (this.dataset.excludedPaths || '')
        .split(',')
        .map((path) => path.trim())
        .filter(Boolean);

      return excludedPaths.some((path) => window.location.pathname.startsWith(path));
    }

    setupTriggers() {
      const trigger = this.dataset.trigger || 'delay_or_exit';
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (trigger === 'delay' || trigger === 'delay_or_exit' || trigger === 'any' || (trigger === 'exit' && isMobile)) {
        this.setupDelayTrigger();
      }
      if (trigger === 'scroll' || trigger === 'any') this.setupScrollTrigger();
      if ((trigger === 'exit' || trigger === 'delay_or_exit' || trigger === 'any') && !isMobile) this.setupExitTrigger();
      if (trigger === 'inactivity') this.setupInactivityTrigger();
    }

    setupDelayTrigger() {
      const timer = window.setTimeout(() => this.open(), Number(this.dataset.delayMs) || 0);
      this.cleanupCallbacks.push(() => window.clearTimeout(timer));
    }

    setupScrollTrigger() {
      const threshold = Number(this.dataset.scrollPercent) || 45;
      const onScroll = () => {
        const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollableHeight <= 0) return;
        const progress = (window.scrollY / scrollableHeight) * 100;
        if (progress >= threshold) this.open();
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this.cleanupCallbacks.push(() => window.removeEventListener('scroll', onScroll));
    }

    setupExitTrigger() {
      const onMouseOut = (event) => {
        if (!event.relatedTarget && event.clientY <= 0) this.open();
      };

      document.addEventListener('mouseout', onMouseOut);
      this.cleanupCallbacks.push(() => document.removeEventListener('mouseout', onMouseOut));
    }

    setupInactivityTrigger() {
      const delay = Number(this.dataset.inactivityMs) || 30000;
      let timer;
      const reset = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => this.open(), delay);
      };
      const events = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];

      events.forEach((eventName) => window.addEventListener(eventName, reset, { passive: true }));
      reset();
      this.cleanupCallbacks.push(() => {
        window.clearTimeout(timer);
        events.forEach((eventName) => window.removeEventListener(eventName, reset));
      });
    }

    open(force = false) {
      if (this.isOpen || (!force && !this.isEligible())) return;

      if (!force && document.body.classList.contains('overflow-hidden')) {
        const retryTimer = window.setTimeout(() => this.open(), 1500);
        this.cleanupCallbacks.push(() => window.clearTimeout(retryTimer));
        return;
      }

      this.clearTriggers();
      this.isOpen = true;
      this.lastFocusedElement = document.activeElement;
      this.overlay.hidden = false;
      this.setSession(this.sessionShownKey, 'true');
      document.body.classList.add('tiger-discount-popup-open');
      document.addEventListener('keydown', this.onKeydown);

      window.requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        (this.emailInput && !this.emailInput.closest('[hidden]') ? this.emailInput : this.dialog).focus();
      });
    }

    close(reason = 'close') {
      if (!this.isOpen) return;

      if (!this.successState || this.successState.hidden) this.recordDismissal();
      this.setSession(this.sessionDismissedKey, 'true');
      this.isOpen = false;
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('tiger-discount-popup-open');
      document.removeEventListener('keydown', this.onKeydown);

      window.setTimeout(() => {
        this.overlay.hidden = true;
        this.lastFocusedElement?.focus?.();
      }, 200);

      this.dispatchEvent(new CustomEvent('tiger:discount-popup:closed', { detail: { reason } }));
    }

    recordDismissal() {
      const days = Number(this.dataset.dismissDays) || 0;
      if (days > 0) this.setStoredNumber(this.dismissKey, Date.now() + days * 86400000);
    }

    recordClaimed() {
      const days = Number(this.dataset.claimedDays) || 365;
      this.setStoredNumber(this.claimedKey, Date.now() + days * 86400000);
      this.setSession(this.sessionShownKey, 'true');
    }

    incrementPageViews() {
      this.setStoredNumber(this.viewsKey, this.getStoredNumber(this.viewsKey) + 1);
    }

    onOverlayClick(event) {
      if (event.target === this.overlay && this.dataset.closeOnOverlay === 'true') this.close('overlay');
    }

    onManualOpen() {
      this.open(true);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.form || this.isSubmitting) return;
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      this.setSubmitting(true);
      this.clearError();

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const returnedPopup = Array.from(responseDocument.querySelectorAll('tiger-discount-popup')).find(
          (popup) => popup.dataset.storageKey === this.storageKey
        );
        const returnedSuccess = returnedPopup?.querySelector('[data-success-state]');
        const returnedError = returnedPopup?.querySelector('[data-popup-error]');
        const wasSuccessful =
          response.ok &&
          (response.url.includes('customer_posted=true') ||
            (returnedSuccess && !returnedSuccess.hasAttribute('hidden')));

        if (!wasSuccessful) {
          throw new Error(returnedError?.textContent?.trim() || 'Please check your email address and try again.');
        }

        this.showSuccessState();
      } catch (error) {
        this.showError(error.message || 'Please check your email address and try again.');
      } finally {
        this.setSubmitting(false);
      }
    }

    showSuccessState() {
      if (!this.offerState || !this.successState) return;
      this.offerState.hidden = true;
      this.successState.hidden = false;
      const successTitle = this.successState.querySelector('[id]');
      if (successTitle) this.dialog.setAttribute('aria-labelledby', successTitle.id);
      this.recordClaimed();
      this.successState.querySelector('button, a[href]')?.focus();
      this.dispatchEvent(new CustomEvent('tiger:discount-popup:claimed'));
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      if (!this.submitButton) return;
      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-busy', String(isSubmitting));
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
      this.emailInput?.removeAttribute('aria-invalid');
      this.emailInput?.removeAttribute('aria-describedby');
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      this.emailInput?.setAttribute('aria-invalid', 'true');
      this.emailInput?.setAttribute('aria-describedby', this.errorMessage.id);
      this.emailInput?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close('escape');
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.closest('[hidden]'));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async copyCode() {
      const code = this.copyButton?.dataset.code || '';
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = code;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }

      const label = this.copyButton.querySelector('[data-copy-label]');
      if (!label) return;
      label.textContent = label.dataset.copiedLabel || 'Copied';
      window.setTimeout(() => {
        label.textContent = label.dataset.defaultLabel || 'Copy code';
      }, 1800);
    }

    clearTriggers() {
      this.cleanupCallbacks.splice(0).forEach((cleanup) => cleanup());
    }

    getStoredNumber(key) {
      try {
        return Number(window.localStorage.getItem(key)) || 0;
      } catch (error) {
        return 0;
      }
    }

    setStoredNumber(key, value) {
      try {
        window.localStorage.setItem(key, String(value));
      } catch (error) {}
    }

    getSession(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    setSession(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {}
    }
  }

  customElements.define('tiger-discount-popup', TigerDiscountPopup);
}
